import { useState } from 'react';
import { 
  ArrowRight, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  X
} from 'lucide-react';
import { ProjectData } from '../types';

interface JiraStatusAdvancerProps {
  project: ProjectData;
  isOpen: boolean;
  onClose: () => void;
  onAdvance: (newStatus: string) => Promise<void>;
}

const STATUS_OPTIONS = [
  { value: 'Build App', label: 'Build App', description: 'Ready for development / build kicked off' },
  { value: 'Building App', label: 'Building App', description: 'App configuration is being built' },
  { value: 'Provide Test Apps', label: 'Provide Test Apps', description: 'App is being tested' },
  { value: 'Internal App Review', label: 'Internal App Review', description: 'Internal review before customer' },
  { value: 'Customer Review', label: 'Customer Review', description: 'Ready for customer review' },
  { value: 'Submit to App Store', label: 'Submit to App Store', description: 'Submitting to App Store' },
  { value: 'Waiting for approval', label: 'Waiting for approval', description: 'Waiting on App Store approval' },
  { value: 'CA Deployed', label: 'CA Deployed', description: 'Customer app has been deployed / finished' },
  { value: 'On Hold', label: 'On Hold', description: 'Temporarily paused' },
];

export function JiraStatusAdvancer({ project, isOpen, onClose, onAdvance }: JiraStatusAdvancerProps) {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [advanceStatus, setAdvanceStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleAdvance = async () => {
    if (!selectedStatus) return;

    try {
      setIsAdvancing(true);
      setAdvanceStatus('idle');
      setErrorMessage('');

      await onAdvance(selectedStatus);
      setAdvanceStatus('success');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error advancing Jira status:', error);
      setAdvanceStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to advance status');
    } finally {
      setIsAdvancing(false);
    }
  };

  const handleClose = () => {
    if (!isAdvancing) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Advance Jira Status
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {project.jiraKey} - {project.customerName}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Current Status: <span className="font-medium">{project.status}</span>
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isAdvancing}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Status Messages */}
          {advanceStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700 text-sm">Status advanced successfully!</span>
            </div>
          )}

          {advanceStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 text-sm">{errorMessage}</span>
            </div>
          )}

          {/* Status Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select New Status
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {STATUS_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedStatus === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={selectedStatus === option.value}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClose}
              disabled={isAdvancing}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAdvance}
              disabled={isAdvancing || !selectedStatus}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
            >
              {isAdvancing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Advancing...
                </>
              ) : (
                <>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Advance Status
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
